import * as React from 'react';
import {
    Box,
    Button,
    Dialog,
    Stack,
    TablePagination,
    Tooltip,
    Typography,
} from '@mui/material';
import Filter from 'component/filter';
import colors from 'assets/theme/base/colors';
import borders from 'assets/theme/base/borders';
import FlexibleTable from 'component/flexible-table';
import { useEffect, useState } from 'react';
import { RowDataType, TableMetaData } from 'types/common/common';
import { useAppDispatch } from 'hooks/redux';
import { ReactComponent as Edit } from 'assets/images/staff-user/edit.svg';
import { ReactComponent as Delete } from 'assets/images/staff-user/delete.svg';
import ArchiveIcon from '@mui/icons-material/Archive';
import AddPhotoAlternateIcon from '@mui/icons-material/AddPhotoAlternate';
import { openConfirmationDialogs } from 'store/slices/confirmation-dialog/confirmation-dialog-slice';
import AddImages from 'component/add-images';
import pxToRem from 'assets/theme/function/px-to-rem';
import { openSnackbar } from 'store/slices/snackbar/snackbar-slice';
import { useNavigate } from 'react-router-dom';
import { ProductAddRequest, ProductData } from 'types/product/product';
import { useLazyProductListQuery } from 'store/slices/product/product-list';
import { useProductCategoryListQuery } from 'store/slices/product/product-enum';
import { useProductImageUpdateMutation } from 'store/slices/product/creat-product-image';
import { useProductActiveMutation } from 'store/slices/product/active-product';
import {
    useProductArchiveMutation,
    useProductDeleteMutation,
} from 'store/slices/product/delete-archive-product';
import { ImageTypeEnum } from 'types/card/card';
import FilterProduct from 'pages/product/products/filter-products';
import { getStatusColor } from 'component/status-style';
import { StyledTableRow } from 'component/tabel-pagination-style';
import { API_ENDPOINT_URL } from 'constant';
import ViewPhoto from 'component/view-photo';
import { UrlObject } from 'pages/card';
import { openBackdrop } from 'store/slices/backdrop/backdrop-slice';

export const initialListData: ProductAddRequest = {};

const defaultTableStaffDataList: TableMetaData[] = [
    {
        key: 'img',
        position: 1,
        title: 'Image',
        isSortable: false,
    },
    {
        key: 'productTitle',
        position: 2,
        title: 'Product Title',
        isSortable: false,
    },
    {
        key: 'productCategory',
        position: 3,
        title: 'Product Category',
        isSortable: false,
    },
    {
        key: 'productNumber',
        position: 4,
        title: 'Product Number',
        isSortable: true,
    },
    {
        key: 'createdDate',
        position: 5,
        title: 'Created Data',
        isSortable: false,
        type: 'date',
        format: 'DD.MM.YYYY HH:mm',
    },
    {
        key: 'status',
        position: 6,
        title: 'Status',
        isSortable: false,
    },
    {
        key: 'action',
        position: 7,
        title: 'Action',
        isSortable: false,
    },
];

const Product: React.FC = () => {
    const [tableMetaDataList, setTableMetaDataList] = useState<TableMetaData[]>(
        defaultTableStaffDataList,
    );
    const [openImgDialog, setOpenImgDialog] = React.useState(false);
    const [productId, setProductId] = React.useState<ProductData>();
    const [dataProductRequestList, setDataProductRequestList] =
        React.useState<ProductAddRequest>(initialListData);
    const [pagesCount, setPagesCount] = useState<number>(0);
    const [page, setPage] = useState<number>(1);
    const [pageSize, setPageSize] = useState<number>(10);
    const [sort, setSort] = useState<string>('productNumber');
    const [tableData, setTableData] = useState<RowDataType[]>([]);
    const [viewPhotoUrl, setViewPhotoUrl] = useState<UrlObject | null>(null);

    const navigate = useNavigate();

    const [productImagesUpdateApi, { isSuccess }] =
        useProductImageUpdateMutation();

    const [getProductList, { data, isFetching }] = useLazyProductListQuery();
    const { data: productCategoryList } = useProductCategoryListQuery();

    const [
        productDeleteApi,
        {
            error: deleteError,
            isError: deleteIsError,
            isSuccess: deleteIsSuccess,
        },
    ] = useProductDeleteMutation();

    const [
        productArchiveApi,
        {
            error: archiveError,
            isError: archiveIsError,
            isSuccess: archiveIsSuccess,
        },
    ] = useProductArchiveMutation();

    const [
        productActiveApi,
        {
            error: activeError,
            isError: activeIsError,
            isSuccess: activeIsSuccess,
        },
    ] = useProductActiveMutation();

    const dispatch = useAppDispatch();

    const createNewProduct = () => {
        navigate('/product/product-detail');
    };

    const updateImages = (images: any) => {
        productImagesUpdateApi({
            productId: productId?.id,
            image: images,
        });
    };

    const deleteProduct = ({ id }: ProductData) => {
        if (id) {
            productDeleteApi(id);
        }
    };

    const archiveProduct = ({ id }: ProductData) => {
        if (id) {
            productArchiveApi(id);
        }
    };

    const handelClickEditProduct = (productId: ProductData) => {
        navigate(`/product/product-detail/${productId.id}`);
    };

    const handelClickStatus = (productActiveId: string) => {
        productActiveApi(productActiveId);
    };

    const openPictureDialogFun = (productId: ProductData) => {
        setProductId(productId);
        setOpenImgDialog(true);
    };

    const handleFilterData = (currentPage = 0) => {
        setPage(currentPage + 1);
    };

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement>,
    ) => {
        setPageSize(Number(event.target.value));
        setPage(1);
    };

    useEffect(() => {
        setPagesCount(
            data && data?.data.length > 0 ? Number(data?.rowsCount) : 0,
        );
        const tableData: RowDataType[] = data
            ? data.data.map((fullInfo) => {
                  return {
                      ...fullInfo,
                      status: (
                          <Box>
                              <Button
                                  variant={'contained'}
                                  size={'small'}
                                  sx={{
                                      bgcolor: getStatusColor(fullInfo.status),
                                      pointerEvents:
                                          fullInfo.status !== 'draft'
                                              ? 'none'
                                              : 'auto',
                                  }}
                                  onClick={() => {
                                      dispatch(
                                          openConfirmationDialogs({
                                              textInfo:
                                                  'Are You Sure Want To Active Product',
                                              handleConfirm: () =>
                                                  handelClickStatus(
                                                      fullInfo.id,
                                                  ),
                                          }),
                                      );
                                  }}
                              >
                                  {fullInfo.status}
                              </Button>
                          </Box>
                      ),
                      img: (
                          <>
                              {fullInfo?.fileId ? (
                                  <Box
                                      component="img"
                                      src={`${API_ENDPOINT_URL}/file/file/${fullInfo?.fileId}?source=${ImageTypeEnum.PRODUCT}`}
                                      alt=""
                                      onClick={() => {
                                          setViewPhotoUrl({
                                              url: `${API_ENDPOINT_URL}/file/file/${fullInfo?.fileId}?source=${ImageTypeEnum.PRODUCT}`,
                                              title: fullInfo.productTitle,
                                          });
                                      }}
                                      sx={{
                                          cursor: 'pointer',
                                          width: 100,
                                          height: 80,
                                          objectFit: 'contain',
                                          objectPosition: 'top',
                                          borderRadius: borders.borderRadius.md,
                                      }}
                                  />
                              ) : (
                                  <Typography variant={'h5'}>-</Typography>
                              )}
                          </>
                      ),
                      action: (
                          <>
                              {fullInfo.status !== 'archive' && (
                                  <Box
                                      display={'flex'}
                                      justifyContent={'center'}
                                      gap={2}
                                  >
                                      {fullInfo.status !== 'draft' ? (
                                          <Tooltip title="Delete">
                                              <Delete
                                                  cursor="pointer"
                                                  onClick={() => {
                                                      dispatch(
                                                          openConfirmationDialogs(
                                                              {
                                                                  textInfo:
                                                                      'Are You Sure Want To Delete Card',
                                                                  handleConfirm:
                                                                      deleteProduct,
                                                                  parameters: {
                                                                      id: fullInfo.id,
                                                                  },
                                                              },
                                                          ),
                                                      );
                                                  }}
                                              />
                                          </Tooltip>
                                      ) : (
                                          <Tooltip title="Archive">
                                              <ArchiveIcon
                                                  cursor="pointer"
                                                  onClick={() => {
                                                      dispatch(
                                                          openConfirmationDialogs(
                                                              {
                                                                  textInfo:
                                                                      'Are You Sure Want To Arcive Card',
                                                                  handleConfirm:
                                                                      archiveProduct,
                                                                  parameters: {
                                                                      id: fullInfo.id,
                                                                  },
                                                              },
                                                          ),
                                                      );
                                                  }}
                                              />
                                          </Tooltip>
                                      )}

                                      <Tooltip title="Edit">
                                          <Edit
                                              cursor="pointer"
                                              onClick={() =>
                                                  handelClickEditProduct({
                                                      fileId: fullInfo.fileId,
                                                      id: fullInfo.id,
                                                  })
                                              }
                                          />
                                      </Tooltip>
                                      <Tooltip title="Add photo">
                                          <AddPhotoAlternateIcon
                                              titleAccess={'Add photo'}
                                              cursor="pointer"
                                              onClick={() =>
                                                  openPictureDialogFun({
                                                      fileId: fullInfo.fileId,
                                                      id: fullInfo.id,
                                                      imageType:
                                                          ImageTypeEnum.PRODUCT,
                                                  })
                                              }
                                          />
                                      </Tooltip>
                                  </Box>
                              )}
                          </>
                      ),
                      rowComponent: StyledTableRow,
                  };
              })
            : [];
        setTableData(tableData);
    }, [data]);

    useEffect(() => {
        if (
            deleteIsSuccess ||
            isSuccess ||
            archiveIsSuccess ||
            activeIsSuccess
        ) {
            getProductList(dataProductRequestList);
            setOpenImgDialog(false);
        }
    }, [deleteIsSuccess, isSuccess, archiveIsSuccess, activeIsSuccess]);

    useEffect(() => {
        if (dataProductRequestList) {
            getProductList({
                ...dataProductRequestList,
                sort: sort,
                page: page,
                pageSize: pageSize,
            });
        }
    }, [dataProductRequestList, page, pageSize, sort]);

    useEffect(() => {
        dispatch(openBackdrop({ open: isFetching }));
    }, [isFetching]);

    useEffect(() => {
        const error = deleteError || archiveError || activeError;

        if (error && 'data' in error && error.data?.message) {
            dispatch(
                openSnackbar({
                    message: error.data.message,
                    severity: 'error',
                    open: true,
                    position: {
                        vertical: 'top',
                        horizontal: 'right',
                    },
                }),
            );
        }
    }, [deleteIsError, archiveIsError, activeIsError]);

    return (
        <Box>
            <Filter>
                <FilterProduct
                    setDataProductRequestList={setDataProductRequestList}
                    productCategoryList={productCategoryList}
                />
            </Filter>
            <Box
                bgcolor={colors.secondary.light}
                borderRadius={borders.borderRadius.md}
                mt={pxToRem(7)}
            >
                <FlexibleTable
                    tableMetaDataList={tableMetaDataList}
                    setTableMetaDataList={setTableMetaDataList}
                    tableDataList={tableData}
                    sort={sort}
                    setSort={setSort}
                    createNewFun={createNewProduct}
                    buttonName={'Creat New Product'}
                />

                {pagesCount > 0 && (
                    <Stack spacing={2} alignItems={'end'}>
                        <TablePagination
                            rowsPerPageOptions={[10, 30, 50]}
                            component="div"
                            count={pagesCount}
                            rowsPerPage={pageSize}
                            page={page - 1}
                            labelRowsPerPage={'Number of rows'}
                            slotProps={{
                                select: {
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    MenuProps: {
                                        disablePortal: true,
                                        slotProps: {
                                            paper: {
                                                sx: {
                                                    backgroundColor:
                                                        colors.secondary.main,
                                                    padding: 0,
                                                },
                                            },
                                        },
                                    },
                                    native: false,
                                },
                            }}
                            onPageChange={(_, page) => handleFilterData(page)}
                            onRowsPerPageChange={handleChangeRowsPerPage}
                        />
                    </Stack>
                )}
            </Box>

            {productId?.id && (
                <>
                    <Dialog
                        open={openImgDialog}
                        onClose={() => {
                            setOpenImgDialog(false);
                        }}
                    >
                        <AddImages
                            imageUrl={[
                                productId.fileId
                                    ? `${API_ENDPOINT_URL}/file/file/${productId.fileId}?source=${productId.imageType}`
                                    : undefined,
                            ]}
                            setOpenImgDialog={setOpenImgDialog}
                            onSubmitFun={updateImages}
                            dialogTitle={'NEW PRODUCT IMAGE'}
                        />
                    </Dialog>
                </>
            )}

            {viewPhotoUrl && (
                <ViewPhoto
                    open={Boolean(viewPhotoUrl)}
                    setOpen={() => setViewPhotoUrl(null)}
                    imageUrl={viewPhotoUrl}
                />
            )}
        </Box>
    );
};

export default Product;
